import React, { useEffect, useRef, useState } from "react";
import { handleGoogleSearch, performSearch } from "./GoogleSearchQuery";
import { Link, useNavigate } from "react-router-dom";
import ROUTES from "../../route";
import PropTypes from "prop-types";
import { useBooks } from "../State/bookContext";

const OutsideClickHandler = ({ children, onClickOutside }) => {
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                onClickOutside();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClickOutside]);

    return <div ref={wrapperRef}>{children}</div>;
};

const SearchBar = () => {
    const { books, setBooks } = useBooks();
    const [search, setSearch] = useState('');
    const [isActive, setIsActive] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!search.trim()) {
            setBooks([]);
            setIsActive(false);
            return;
        }
        const timer = setTimeout(() => {
            performSearch(search, null, setBooks, setIsActive, setError);
        }, 1000);
        return () => clearTimeout(timer);
    }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleOutsideClick = () => { setIsActive(false); setError(null); };

    const handleClickRedirection = () => {
        navigate(ROUTES.SEARCH_BOOK, { state: { initialBooks: books, initialQuery: search } });
        setIsActive(false);
    };

    return (
        <div className="relative">
            <div className="relative">
                <input
                    onKeyDown={(e) => handleGoogleSearch(e, setBooks, setIsActive, setError)}
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setError(null); }}
                    type="search"
                    placeholder="Search books…"
                    className="w-64 px-4 py-2 pl-9 pr-8 bg-warm-50 border border-warm-200 rounded-full text-sm text-warm-800 placeholder-warm-300 focus:outline-none focus:ring-2 focus:ring-warm-400 focus:border-transparent transition"
                />
                <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-warm-300"
                    fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                </svg>
                {search && (
                    <button
                        onClick={() => { setSearch(''); setBooks([]); setIsActive(false); setError(null); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-warm-300 hover:text-warm-500 transition-colors"
                        aria-label="Clear search"
                    >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}
            </div>

            {error && (
                <OutsideClickHandler onClickOutside={handleOutsideClick}>
                    <div className="absolute top-full left-0 right-0 mt-2 bg-red-50 border border-red-200 rounded-xl shadow-xl z-50 min-w-[360px] px-4 py-3">
                        <p className="text-red-600 text-sm font-medium">{error}</p>
                    </div>
                </OutsideClickHandler>
            )}

            {!error && books.length > 0 && isActive && (
                <OutsideClickHandler onClickOutside={handleOutsideClick}>
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-warm-200 rounded-xl shadow-xl max-h-80 overflow-y-auto z-50 min-w-[360px]">
                        {books.slice(0, 8).map((book, index) => (
                            <Link
                                key={index}
                                to={{ pathname: ROUTES.GOOGLE_BOOK }}
                                state={{ book }}
                                onClick={() => { setSearch(''); setBooks([]); setIsActive(false); }}
                                className="flex items-start gap-3 px-4 py-3 hover:bg-warm-50 transition-colors border-b border-warm-100 last:border-0"
                            >
                                {book.volumeInfo.imageLinks?.thumbnail ? (
                                    <img
                                        src={book.volumeInfo.imageLinks.thumbnail}
                                        alt=""
                                        className="w-10 h-14 object-cover rounded flex-shrink-0"
                                    />
                                ) : (
                                    <div className="w-10 h-14 bg-warm-100 rounded flex items-center justify-center flex-shrink-0">
                                        <span className="text-lg">📖</span>
                                    </div>
                                )}
                                <div className="min-w-0">
                                    <p className="font-medium text-warm-800 text-sm truncate">{book.volumeInfo.title}</p>
                                    {book.volumeInfo.authors && (
                                        <p className="text-warm-400 text-xs mt-0.5 truncate">{book.volumeInfo.authors.join(', ')}</p>
                                    )}
                                    {book.volumeInfo.publishedDate && (
                                        <p className="text-warm-300 text-xs">{book.volumeInfo.publishedDate}</p>
                                    )}
                                </div>
                            </Link>
                        ))}
                        {books.length > 8 && (
                            <button
                                onClick={handleClickRedirection}
                                className="w-full px-4 py-3 text-sm text-warm-600 hover:bg-warm-50 font-medium transition-colors text-center"
                            >
                                See all {books.length} results →
                            </button>
                        )}
                    </div>
                </OutsideClickHandler>
            )}
        </div>
    );
};

SearchBar.propTypes = {
    books: PropTypes.array,
    setBooks: PropTypes.func,
};

export default SearchBar;
